import { css, DefaultTheme, FlattenInterpolation, ThemeProps } from 'styled-components';

export type ArticleStyleProps = {
    $accent: string;
    $index: number;
};

export type StyleProps = ArticleStyleProps & ThemeProps<DefaultTheme>;
export type Rule = FlattenInterpolation<StyleProps>;

export type ArticleVariant = {
    id: number;
    label: string;
    cardWidth: string;
    cardHeight: string;
    gap: string;
    /* Seconds a single card takes to cross its own width; sets the marquee speed. */
    secondsPerCard: number;
    pauseOnHover?: boolean;
    fadeEdges?: boolean;
    numbered?: boolean;
    rail?: Rule;
    track?: Rule;
    card?: Rule;
    header?: Rule;
    title?: Rule;
    description?: Rule;
    tags?: Rule;
    tag?: Rule;
    number?: Rule;
};

const lifted = (amount: string) => css<StyleProps>`
    background: color-mix(in srgb, ${({ theme }) => theme.colour.OFF_WHITE} ${amount}, ${({ theme }) => theme.colour.DARK_GREY});
`;

const mono = css<StyleProps>`
    font-family: ${({ theme }) => theme.font.FONT_MONO_JETBRAINS};
`;

const eyebrow = css<StyleProps>`
    ${mono}
    font-size: 0.62em;
    letter-spacing: 0.18em;
    text-transform: uppercase;
`;

export const ARTICLE_VARIANTS: ArticleVariant[] = [
    {
        id: 1,
        label: '01 — hairline cards',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '1rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            transition: border-color 220ms ease;
            &:hover {
                border-color: ${({ $accent }) => $accent};
            }
            &:hover h2 {
                color: ${({ $accent }) => $accent};
            }
        `,
        title: css<StyleProps>`
            transition: color 220ms ease;
        `,
    },
    {
        id: 2,
        label: '02 — ledger rules',
        cardWidth: '22rem',
        cardHeight: '18rem',
        gap: '0rem',
        secondsPerCard: 7.5,
        fadeEdges: true,
        card: css<StyleProps>`
            border-right: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            padding: 1.4em 2em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 3,
        label: '03 — accent spine',
        cardWidth: '20rem',
        cardHeight: '19rem',
        gap: '0.9rem',
        secondsPerCard: 6.5,
        fadeEdges: true,
        card: css<StyleProps>`
            ${lifted('5%')}
            border-left: 3px solid ${({ $accent }) => $accent};
            border-radius: 0 ${({ theme }) => theme.BORDER_RADIUS} ${({ theme }) => theme.BORDER_RADIUS} 0;
            padding-left: 1.5em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 4,
        label: '04 — index plates',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1rem',
        secondsPerCard: 7,
        numbered: true,
        fadeEdges: true,
        card: css<StyleProps>`
            ${lifted('4%')}
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding-top: 1.6em;
        `,
        number: css<StyleProps>`
            top: 0.2em;
            right: 0.3em;
            font-size: 4.5em;
            font-weight: 700;
            opacity: 0.14;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 5,
        label: '05 — terminal blocks',
        cardWidth: '23rem',
        cardHeight: '19rem',
        gap: '1.2rem',
        secondsPerCard: 7.5,
        card: css<StyleProps>`
            border: 1px dashed ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 1.3em 1.4em;
        `,
        header: css<StyleProps>`
            ${mono}
            font-size: 0.7em;
            color: ${({ $accent }) => $accent};
            &::before {
                content: '$ ';
                color: ${({ theme }) => theme.colour.LIGHT_GREY};
            }
        `,
        title: css<StyleProps>`
            ${mono}
            font-size: 1.15em;
            font-weight: 500;
            letter-spacing: -0.02em;
        `,
        description: css<StyleProps>`
            ${mono}
            font-size: 0.72em;
            line-height: 1.7;
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
    },
    {
        id: 6,
        label: '06 — stacked slabs',
        cardWidth: '24rem',
        cardHeight: '21rem',
        gap: '0.6rem',
        secondsPerCard: 8,
        card: css<StyleProps>`
            ${lifted('7%')}
            border-radius: 0;
            padding: 1.8em 1.6em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        title: css<StyleProps>`
            font-size: 1.9em;
            line-height: 1.1;
            padding-bottom: 0.35em;
            border-bottom: 4px solid ${({ $accent }) => $accent};
        `,
    },
    {
        id: 7,
        label: '07 — dotted field',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '1rem',
        secondsPerCard: 6.8,
        fadeEdges: true,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            background-image: radial-gradient(
                ${({ theme }) => theme.colour.LIGHT_GREY} 1px,
                transparent 1px
            );
            background-size: 12px 12px;
            background-position: -1px -1px;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
        title: css<StyleProps>`
            text-shadow: 0 0 0.6em ${({ theme }) => theme.colour.DARK_GREY};
        `,
    },
    {
        id: 8,
        label: '08 — accent wash',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '0.9rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            &::before {
                content: '';
                position: absolute;
                inset: 0;
                background: linear-gradient(155deg, ${({ $accent }) => $accent} 0%, transparent 65%);
                opacity: 0.16;
                pointer-events: none;
            }
            > * {
                position: relative;
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 9,
        label: '09 — corner brackets',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1.4rem',
        secondsPerCard: 7.5,
        fadeEdges: true,
        card: css<StyleProps>`
            border-radius: 0;
            padding: 1.6em;
            &::before,
            &::after {
                content: '';
                position: absolute;
                width: 1.6em;
                height: 1.6em;
                border: 1px solid ${({ $accent }) => $accent};
                pointer-events: none;
            }
            &::before {
                top: 0.5em;
                left: 0.5em;
                border-right: none;
                border-bottom: none;
            }
            &::after {
                bottom: 0.5em;
                right: 0.5em;
                border-left: none;
                border-top: none;
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 10,
        label: '10 — over rule',
        cardWidth: '20rem',
        cardHeight: '18rem',
        gap: '1.6rem',
        secondsPerCard: 6.5,
        card: css<StyleProps>`
            border-radius: 0;
            border-top: 2px solid ${({ $accent }) => $accent};
            padding: 1.2em 0 0 0;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        tags: css<StyleProps>`
            border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            margin-top: auto;
            padding-top: 0.9em;
        `,
    },
    {
        id: 11,
        label: '11 — filmstrip',
        cardWidth: '17rem',
        cardHeight: '22rem',
        gap: '0.5rem',
        secondsPerCard: 5.5,
        fadeEdges: true,
        card: css<StyleProps>`
            ${lifted('5%')}
            border-radius: 0;
            padding: 2.2em 1.3em;
            &::before,
            &::after {
                content: '';
                position: absolute;
                left: 0;
                right: 0;
                height: 1.1em;
                background-image: radial-gradient(
                    circle,
                    ${({ theme }) => theme.colour.DARK_GREY} 0.3em,
                    transparent 0.32em
                );
                background-size: 1.6em 1.1em;
            }
            &::before {
                top: 0;
            }
            &::after {
                bottom: 0;
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
        title: css<StyleProps>`
            font-size: 1.25em;
        `,
        tags: css<StyleProps>`
            padding-bottom: 0.4em;
        `,
    },
    {
        id: 12,
        label: '12 — swatch headers',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '1rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
        `,
        header: css<StyleProps>`
            ${eyebrow}
            display: flex;
            align-items: center;
            gap: 0.6em;
            &::before {
                content: '';
                width: 0.8em;
                height: 0.8em;
                flex: none;
                background: ${({ $accent }) => $accent};
            }
        `,
    },
    {
        id: 13,
        label: '13 — inverse cards',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '1.1rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            background: ${({ theme }) => theme.colour.OFF_WHITE};
            color: ${({ theme }) => theme.colour.DARK_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            border-bottom: 5px solid ${({ $accent }) => $accent};
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        description: css<StyleProps>`
            color: ${({ theme }) => theme.colour.DARK_GREY};
            opacity: 0.8;
        `,
    },
    {
        id: 14,
        label: '14 — mono manifest',
        cardWidth: '24rem',
        cardHeight: '17rem',
        gap: '1.4rem',
        secondsPerCard: 8,
        numbered: true,
        card: css<StyleProps>`
            border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-bottom: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            padding: 1.4em 0;
        `,
        number: css<StyleProps>`
            position: static;
            display: block;
            font-size: 0.7em;
            opacity: 1;
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
            margin-bottom: 0.6em;
        `,
        header: css<StyleProps>`
            ${mono}
            font-size: 0.65em;
            letter-spacing: 0.22em;
            text-transform: uppercase;
            color: ${({ $accent }) => $accent};
        `,
        title: css<StyleProps>`
            ${mono}
            font-weight: 400;
            font-size: 1.05em;
        `,
        description: css<StyleProps>`
            ${mono}
            font-size: 0.7em;
            line-height: 1.8;
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
            &::before {
                content: '> ';
                color: ${({ $accent }) => $accent};
            }
        `,
        tag: css<StyleProps>`
            height: 1.8em;
        `,
    },
    {
        id: 15,
        label: '15 — gradient dividers',
        cardWidth: '22rem',
        cardHeight: '19rem',
        gap: '0rem',
        secondsPerCard: 7.5,
        fadeEdges: true,
        card: css<StyleProps>`
            border-radius: 0;
            padding: 1.4em 2.2em;
            &::after {
                content: '';
                position: absolute;
                top: 15%;
                bottom: 15%;
                right: 0;
                width: 1px;
                background: linear-gradient(
                    to bottom,
                    transparent,
                    ${({ $accent }) => $accent},
                    transparent
                );
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 16,
        label: '16 — tall editorial',
        cardWidth: '19rem',
        cardHeight: '24rem',
        gap: '1.2rem',
        secondsPerCard: 6,
        fadeEdges: true,
        card: css<StyleProps>`
            ${lifted('6%')}
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 1.6em 1.4em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
            margin-bottom: 1.2em;
        `,
        title: css<StyleProps>`
            font-size: 1.7em;
            line-height: 1.15;
            margin-bottom: 0.7em;
        `,
        description: css<StyleProps>`
            font-size: 0.8em;
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        tags: css<StyleProps>`
            border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            padding-top: 1em;
        `,
    },
    {
        id: 17,
        label: '17 — hover lift',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '1.2rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            border: 1px solid transparent;
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            ${lifted('4%')}
            transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease;
            &:hover {
                transform: translateY(-0.5em);
                border-color: ${({ $accent }) => $accent};
                box-shadow: 0 0.8em 1.6em rgba(0, 0, 0, 0.45);
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 18,
        label: '18 — datasheet',
        cardWidth: '23rem',
        cardHeight: '19rem',
        gap: '0.8rem',
        secondsPerCard: 7.5,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            padding: 0;
        `,
        header: css<StyleProps>`
            ${mono}
            font-size: 0.62em;
            letter-spacing: 0.24em;
            text-transform: uppercase;
            color: ${({ theme }) => theme.colour.DARK_GREY};
            background: ${({ $accent }) => $accent};
            margin: 0;
            padding: 0.7em 1.4em;
        `,
        title: css<StyleProps>`
            font-size: 1.25em;
            margin: 0;
            padding: 0.9em 1.2em 0.6em 1.2em;
        `,
        description: css<StyleProps>`
            font-size: 0.82em;
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
            border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            padding: 0.9em 1.2em;
        `,
        tags: css<StyleProps>`
            border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            padding: 0.8em 1.2em;
        `,
    },
    {
        id: 19,
        label: '19 — chamfered panels',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1.1rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            ${lifted('8%')}
            border-radius: 0;
            padding: 1.5em;
            clip-path: polygon(0 0, calc(100% - 1.6em) 0, 100% 1.6em, 100% 100%, 1.6em 100%, 0 calc(100% - 1.6em));
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
        title: css<StyleProps>`
            font-size: 1.4em;
            padding-right: 1.2em;
        `,
    },
    {
        id: 20,
        label: '20 — blueprint wire',
        cardWidth: '21rem',
        cardHeight: '22rem',
        gap: '1.6rem',
        secondsPerCard: 6.5,
        fadeEdges: true,
        card: css<StyleProps>`
            background: transparent;
            overflow: visible;
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            padding: 1.5em;
            transition: border-color 220ms ease;
            &::before,
            &::after {
                ${mono}
                content: '+';
                position: absolute;
                font-size: 0.9em;
                line-height: 1;
                color: ${({ $accent }) => $accent};
            }
            &::before {
                top: -0.45em;
                left: -0.45em;
            }
            &::after {
                bottom: -0.45em;
                right: -0.45em;
            }
            &:hover {
                border-color: ${({ $accent }) => $accent};
            }
        `,
        header: css<StyleProps>`
            ${mono}
            font-size: 0.62em;
            letter-spacing: 0.2em;
            text-transform: uppercase;
        `,
        title: css<StyleProps>`
            font-size: 1.35em;
            font-weight: 400;
        `,
        description: css<StyleProps>`
            font-size: 0.82em;
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        tag: css<StyleProps>`
            height: 2em;
            opacity: 0.8;
        `,
    },
    {
        id: 21,
        label: '21 — split rule',
        cardWidth: '22rem',
        cardHeight: '19rem',
        gap: '1rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 0 1.4em 1.4em 1.4em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            margin: 0 -1.4em 1.3em -1.4em;
            padding: 1em 1.4em;
            border-bottom: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 22,
        label: '22 — corner badge',
        cardWidth: '21rem',
        cardHeight: '20rem',
        gap: '1.2rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            ${lifted('5%')}
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding-top: 2.6em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            position: absolute;
            top: 1em;
            left: 1.2em;
            margin: 0;
            padding: 0.4em 0.8em;
            border-radius: 999px;
            color: ${({ theme }) => theme.colour.DARK_GREY};
            background: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 23,
        label: '23 — outlined titles',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1.2rem',
        secondsPerCard: 6.5,
        fadeEdges: true,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            transition: background 220ms ease;
        `,
        title: css<StyleProps>`
            font-size: 1.8em;
            color: transparent;
            -webkit-text-stroke: 1px ${({ $accent }) => $accent};
            transition: color 220ms ease;
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 24,
        label: '24 — weighted base',
        cardWidth: '21rem',
        cardHeight: '20rem',
        gap: '0.9rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            border-bottom: 0.7em solid ${({ $accent }) => $accent};
            ${lifted('6%')}
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
        tags: css<StyleProps>`
            padding-bottom: 0.2em;
        `,
    },
    {
        id: 25,
        label: '25 — ticket stub',
        cardWidth: '23rem',
        cardHeight: '19rem',
        gap: '1.4rem',
        secondsPerCard: 7.5,
        numbered: true,
        card: css<StyleProps>`
            ${lifted('5%')}
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding-left: 3.4em;
            &::before {
                content: '';
                position: absolute;
                top: 0.8em;
                bottom: 0.8em;
                left: 2.4em;
                border-left: 1px dashed ${({ theme }) => theme.colour.LIGHT_GREY};
            }
        `,
        number: css<StyleProps>`
            top: 1.2em;
            left: 0.7em;
            right: auto;
            font-size: 0.8em;
            opacity: 1;
            writing-mode: vertical-rl;
            letter-spacing: 0.3em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 26,
        label: '26 — soft halo',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '1.6rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            ${lifted('5%')}
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            transition: box-shadow 280ms ease;
            &:hover {
                box-shadow: 0 0 0 1px ${({ $accent }) => $accent},
                    0 0 2.4em -0.4em ${({ $accent }) => $accent};
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 27,
        label: '27 — numbered gutter',
        cardWidth: '23rem',
        cardHeight: '19rem',
        gap: '1rem',
        secondsPerCard: 7.5,
        numbered: true,
        card: css<StyleProps>`
            border-left: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            padding-left: 4em;
        `,
        number: css<StyleProps>`
            top: 1.1em;
            left: 1.1em;
            right: auto;
            font-size: 1.1em;
            opacity: 1;
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 28,
        label: '28 — type led',
        cardWidth: '24rem',
        cardHeight: '21rem',
        gap: '0.8rem',
        secondsPerCard: 8,
        numbered: true,
        card: css<StyleProps>`
            border-radius: 0;
            border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            padding: 1.6em 1.4em 1.4em 1.4em;
        `,
        number: css<StyleProps>`
            position: static;
            display: block;
            font-size: 3.4em;
            font-weight: 700;
            opacity: 1;
            line-height: 0.8;
            margin-bottom: 0.15em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        title: css<StyleProps>`
            font-size: 1.3em;
        `,
    },
    {
        id: 29,
        label: '29 — half tint',
        cardWidth: '21rem',
        cardHeight: '20rem',
        gap: '1rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            &::before {
                content: '';
                position: absolute;
                inset: 0 0 55% 0;
                background: ${({ $accent }) => $accent};
                opacity: 0.18;
                pointer-events: none;
            }
            > * {
                position: relative;
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ theme }) => theme.colour.OFF_WHITE};
        `,
    },
    {
        id: 30,
        label: '30 — cross grid',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            background-image:
                linear-gradient(to right, ${({ theme }) => theme.colour.LIGHT_GREY} 1px, transparent 1px),
                linear-gradient(to bottom, ${({ theme }) => theme.colour.LIGHT_GREY} 1px, transparent 1px);
            background-size: 3em 3em;
            opacity: 0.98;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
        title: css<StyleProps>`
            text-shadow: 0 0 0.5em ${({ theme }) => theme.colour.DARK_GREY};
        `,
    },
    {
        id: 31,
        label: '31 — stencil caps',
        cardWidth: '22rem',
        cardHeight: '19rem',
        gap: '1.4rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            border-radius: 0;
            border-top: 1px solid ${({ $accent }) => $accent};
            border-bottom: 1px solid ${({ $accent }) => $accent};
            padding: 1.5em 1.2em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        title: css<StyleProps>`
            ${mono}
            font-size: 1.05em;
            font-weight: 400;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            line-height: 1.5;
        `,
    },
    {
        id: 32,
        label: '32 — pill tags',
        cardWidth: '21rem',
        cardHeight: '20rem',
        gap: '1.2rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0.8em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
        tags: css<StyleProps>`
            gap: 0.4em;
        `,
        tag: css<StyleProps>`
            height: 1.8em;
            padding: 0.4em;
            box-sizing: content-box;
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 999px;
        `,
    },
    {
        id: 33,
        label: '33 — hazard corner',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1.1rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            ${lifted('5%')}
            border-radius: 0;
            &::before {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                width: 4em;
                height: 0.5em;
                background: repeating-linear-gradient(
                    -45deg,
                    ${({ $accent }) => $accent} 0 0.35em,
                    transparent 0.35em 0.7em
                );
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 34,
        label: '34 — dot column',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            border-radius: 0;
            padding-left: 2.6em;
            &::before {
                content: '';
                position: absolute;
                top: 1.2em;
                bottom: 1.2em;
                left: 1.1em;
                width: 0.35em;
                background-image: radial-gradient(
                    circle,
                    ${({ $accent }) => $accent} 0.17em,
                    transparent 0.18em
                );
                background-size: 0.35em 0.9em;
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 35,
        label: '35 — condensed rows',
        cardWidth: '19rem',
        cardHeight: '15rem',
        gap: '0.8rem',
        secondsPerCard: 6.8,
        fadeEdges: true,
        card: css<StyleProps>`
            ${lifted('5%')}
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 1em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
            margin-bottom: 0.3em;
        `,
        title: css<StyleProps>`
            font-size: 1.15em;
            margin-bottom: 0.3em;
        `,
        description: css<StyleProps>`
            font-size: 0.72em;
            color: ${({ theme }) => theme.colour.LIGHT_GREY};
        `,
        tags: css<StyleProps>`
            padding-top: 0.6em;
        `,
        tag: css<StyleProps>`
            height: 1.6em;
        `,
    },
    {
        id: 36,
        label: '36 — folder tab',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1.3rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            ${lifted('6%')}
            overflow: visible;
            border-radius: 0 ${({ theme }) => theme.BORDER_RADIUS} 0 0;
            margin-top: 1.6em;
            padding-top: 1.4em;
            &::before {
                content: '';
                position: absolute;
                top: -1.6em;
                left: 0;
                width: 45%;
                height: 1.6em;
                border-radius: ${({ theme }) => theme.BORDER_RADIUS} ${({ theme }) => theme.BORDER_RADIUS} 0 0;
                background: ${({ $accent }) => $accent};
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 37,
        label: '37 — double frame',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '1.4rem',
        secondsPerCard: 7,
        fadeEdges: true,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: 0;
            padding: 1.9em;
            &::before {
                content: '';
                position: absolute;
                inset: 0.55em;
                border: 1px solid ${({ $accent }) => $accent};
                opacity: 0.45;
                pointer-events: none;
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
        `,
    },
    {
        id: 38,
        label: '38 — pull quote',
        cardWidth: '23rem',
        cardHeight: '21rem',
        gap: '1.2rem',
        secondsPerCard: 7.5,
        card: css<StyleProps>`
            ${lifted('4%')}
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            padding: 1.4em;
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
        title: css<StyleProps>`
            font-size: 1.3em;
        `,
        description: css<StyleProps>`
            font-family: ${({ theme }) => theme.font.FONT_SERIF_ROBOTO_SERIF};
            font-size: 1em;
            font-style: italic;
            line-height: 1.6;
            padding-left: 1.4em;
            border-left: 2px solid ${({ $accent }) => $accent};
        `,
    },
    {
        id: 39,
        label: '39 — ghosted',
        cardWidth: '21rem',
        cardHeight: '19rem',
        gap: '1.2rem',
        secondsPerCard: 6.5,
        fadeEdges: true,
        card: css<StyleProps>`
            border: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
            border-radius: ${({ theme }) => theme.BORDER_RADIUS};
            opacity: 0.45;
            transition: opacity 260ms ease, border-color 260ms ease;
            &:hover {
                opacity: 1;
                border-color: ${({ $accent }) => $accent};
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
    },
    {
        id: 40,
        label: '40 — measure marks',
        cardWidth: '22rem',
        cardHeight: '20rem',
        gap: '0.6rem',
        secondsPerCard: 7,
        card: css<StyleProps>`
            border-radius: 0;
            padding-bottom: 2.2em;
            &::after {
                content: '';
                position: absolute;
                left: 0;
                right: 0;
                bottom: 0;
                height: 0.9em;
                border-top: 1px solid ${({ theme }) => theme.colour.LIGHT_GREY};
                background-image: linear-gradient(
                    to right,
                    ${({ theme }) => theme.colour.LIGHT_GREY} 1px,
                    transparent 1px
                );
                background-size: 1.1em 0.45em;
                background-repeat: repeat-x;
            }
        `,
        header: css<StyleProps>`
            ${eyebrow}
            color: ${({ $accent }) => $accent};
        `,
    },
];

export const DEFAULT_ARTICLE_VARIANT = 35;

export const getArticleVariant = (id?: number): ArticleVariant =>
    ARTICLE_VARIANTS.find((variant) => variant.id === id) ??
    ARTICLE_VARIANTS.find((variant) => variant.id === DEFAULT_ARTICLE_VARIANT) ??
    ARTICLE_VARIANTS[0];
